/**
 * @fileoverview Hook Actions Layer para abrir otro canal del mismo customer desde
 * el header del chat.
 * @module useCustomerChannelsActions
 * @category Hook - Actions Layer
 * @since 3.2.0
 * @version 3.2.0
 */
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setExternalConversation } from '../../../../actions/inbox/conversationActions';
import useInboxChatListActions from './useInboxChatListActions';

/**
 * Hook Actions Layer para el selector de canales del customer.
 *
 * Reusa `handleSelectConversation` de la lista de chats (limpia mensajes, marca
 * leído, carga la primera página y hace focus del composer), pero primero deja la
 * conversación en el slot externo del store: los chats de otro canal no están en
 * `conversationsReducer.data` porque la lista está filtrada por canal y paginada.
 *
 * @function useCustomerChannelsActions
 * @returns {{ handleSelectChannel: function }}
 *
 * @see useCustomerChannelsData
 * @see useSelectedConversation
 * @since 3.2.0
 */
export default function useCustomerChannelsActions() {
  const dispatch = useDispatch();
  const { handleSelectConversation } = useInboxChatListActions();
  // Mismo guard que usa handleSelectConversation: si hay mensajes cargando la
  // selección se descarta, y despachar igual dejaría el slot con un chat que nunca
  // se abrió.
  const messagesLoading = useSelector((state) => state.conversationMessagesReducer.loading);

  /**
   * Abre el chat del canal indicado.
   *
   * El orden importa: si se seleccionara antes de poblar el slot habría un render
   * intermedio sin conversación y la ventana de chat montaría el empty state.
   *
   * @param {Object} channel - Canal devuelto por `useCustomerChannelsData`.
   */
  const handleSelectChannel = useCallback((channel) => {
    if (!channel?.id || messagesLoading) return;

    dispatch(setExternalConversation(channel.raw));
    handleSelectConversation({
      id: channel.id,
      platform: channel.platform,
      customerId: channel.customerId,
      isUnRead: channel.isUnRead
    });
  }, [dispatch, handleSelectConversation, messagesLoading]);

  return { handleSelectChannel };
}
